import React, { useCallback, useContext } from 'react';
import type { FC } from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl-next';
import { Query } from 'react-apollo';

import { DropdownItem } from '@atlaskit/dropdown-menu';
import { Box, xcss } from '@atlaskit/primitives';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import Tooltip from '@atlaskit/tooltip';
import PresenterModeIcon from '@atlaskit/icon/core/presenter-mode';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ArchivePagesLoader,
	ArchiveUpsellDialog,
	canUnarchivePage,
	ShowArchiveCTAQuery,
} from '@confluence/archive-pages';
import { DeleteNestedPagesActionLoader } from '@confluence/bulk-page-actions';
import { LoadableLazy } from '@confluence/loadable';
import { pageAnalyticsUrlToRouteUrl } from '@confluence/confluence-analytics/entry-points/routeUtils';
import { CopyPageTreeDialog } from '@confluence/copy-page-tree-dialog';
import { EditorMovePageButtonLoader } from '@confluence/editor-move-page-button';
import { EditLabelDropdownItem } from '@confluence/labels';
import {
	ResolvedInlineCommentsCounterLoader,
	ResolvedInlineCommentsDialogLoader,
} from '@confluence/resolved-inline-comments-dialog';
import { SessionData } from '@confluence/session-data';
import { AnalyticsPluginWrapper } from '@confluence/plugin-wrappers';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { ContentOwnerPopup, CONTENT_OWNERSHIP_WEB_ITEM_ID } from '@confluence/content-ownership';
import { ViewChangesDialogLoader } from '@confluence/view-changes-dialog';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { useShowCommentsPanel } from '@confluence/comments-panel';
import { ViewValues } from '@confluence/comments-panel-utils';
import { ErrorDisplay, ErrorBoundary, Attribution } from '@confluence/error-boundary';
import { ANALYTICS_ADDON_ACTIONS_MENU_ID } from '@confluence/web-item';
import type { useWebItemLocationWebItemType } from '@confluence/web-item-location';
import { CUSTOM_CONTENTS_ON_PAGE_LIST } from '@confluence/named-routes';
import {
	usePublishModalMountActions,
	usePrePublishTitleValidation,
} from '@confluence/publish-modal';
import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags';
import type { FlagsStateContainer } from '@confluence/flags';
import { ShortcutVisualizer, getShortcutString, EDITOR2_SHORTCUTS } from '@confluence/shortcuts';
import type { RetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n';
import { LazyPresenterModeContextConsumer } from '@confluence/presentation-mode';
import {
	ExperienceTrackerContext,
	PRESENTER_MODE_VIEW_EXPERIENCE,
} from '@confluence/experience-tracker';
import { getSystemPreferredColorScheme } from '@confluence/theming-utils';
import { useThemingState } from '@confluence/theming/entry-points/useThemingState';
import { useSpaceId } from '@confluence/space-utils';

import { ExpandableSubMenuItem } from './ExpandableSubMenuItem';
import type { ExpandableSubMenuItemItemType } from './ExpandableSubMenuItem';
import { PageAppsExpandableSubMenuItems } from './PageAppsExpandableSubMenuItems';
import { i18n } from './sharedDropdowni18n';

export const i18nBase = defineMessages({
	viewChangesTitle: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.view.changes.title',
		description: 'heading of view changes dialog in a live page',
		defaultMessage: 'Latest changes',
	},
	copy: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.copy',
		description: 'The label of Copy button inside ellipsis dropdown of the live pages editor',
		defaultMessage: 'Copy',
	},
	export: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.export',
		description:
			'The text that is displayed in the menu dropdown that group Export to PDF and Export to Word',
		defaultMessage: 'Export',
	},
	advanced: {
		defaultMessage: 'Advanced details',
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.advanced',
		description:
			'The text that display in the live pages advanced details menu dropdown that group Page Information, Page Information, View Source and View Content API',
	},
	publish: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.publish',
		description: 'The label of Publish button inside ellipsis dropdown of the live pages editor',
		defaultMessage: 'Publish',
	},
	publishDots: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.publish-dots',
		description:
			'The label of Publish button inside ellipsis dropdown of the live pages editor, when a modal will open after it is clicked',
		defaultMessage: 'Publish...',
	},
	archive: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.archive',
		description: 'The label of Archive button inside ellipsis dropdown of the live pages editor',
		defaultMessage: 'Archive',
	},
	attachments: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.attachments',
		description: 'The text that display in the menu dropdown that group Files and Custom Contents',
		defaultMessage: 'Attachments',
	},
	files: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.files',
		defaultMessage: 'Files',
		description:
			'The text for Files that display in the menu dropdown that group Files and Custom Contents',
	},
	customContents: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.custom.contents',
		defaultMessage: 'Custom Contents',
		description:
			'The text for Custom Contents that display in the menu dropdown that group Files and Custom Contents',
	},
	pageApps: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.page.apps',
		defaultMessage: 'Apps',
		description:
			'The text in dropdown menu that displays all the apps that are available for the page',
	},
	presenterMode: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.presenter-mode',
		description: 'The label of Presenter mode button inside ellipsis dropdown of live docs',
		defaultMessage: 'Present',
	},
});
export const i18nLiveDocs = defineMessages({
	publish: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.publish.live-docs',
		description:
			'The label of "Convert to page" button inside ellipsis dropdown of the live docs editor',
		defaultMessage: 'Convert to page',
	},
	publishDots: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.publish-dots.live-docs',
		description:
			'The label of "Convert to page" button inside ellipsis dropdown of the live docs editor, when a modal will open after it is clicked',
		defaultMessage: 'Convert to page...',
	},
});
export const livePagesi18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

type ViewChangesItemProps = {
	contentId: string;
	dialogs: DialogsStateContainer;
	saveDraft?: () => void;
};

type AttachmentsMenuItemProps = {
	contentId: string;
	spaceKey: string;
	webItems: useWebItemLocationWebItemType[];
	isSubMenuOpen: boolean;
	handleMouseEnter: () => void;
	handleClick: () => void;
	onOpenChange: () => void;
};

const menuItemContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	gap: 'space.200',
});

const resolvedCommentCountStyles = xcss({
	// min width is needed here to prevent layout shift when the count is loaded
	minWidth: '20px',
});
const analyticsSource = 'LivePagesEllipsisDropdownComponent';

const numbersRegex = /\d+/g;

const PublishButtonSpotlightLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PublishButtonSpotlight" */ './LivePagesPublishSpotlight'
			)
		).LivePagesPublishSpotlight,
});

type PublishItemProps = {
	onClickPublish: (args: { suppressNotifyWatchers?: boolean; message?: string }) => () => void;
	contentId: string;
	saveDraft: (() => void) | undefined;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	optimisticTitle: string | null;
	flags?: FlagsStateContainer;
};

export const PublishItem = withFlags<PublishItemProps>(
	({ contentId, saveDraft, retrieveEditorTitleAndContent, optimisticTitle, flags }) => {
		const { openPublishModal } = usePublishModalMountActions();

		const validatePageAndOpenModal = usePrePublishTitleValidation({
			contentId,
			contentType: 'page',
			isUnpublishedDraft: false,
			flags,
			retrieveEditorTitleAndContent,
			onBeforeValidation: useCallback(() => {
				void saveDraft?.();
			}, [saveDraft]),
			onValidationSuccess: openPublishModal,
			analyticsSource: 'LivePagesEllipsis',
			optimisticTitle,
		});

		return (
			<SpotlightManager blanketIsTinted={false}>
				<SpotlightTarget name="live-pages-onboarding.publish-button-in-overflow">
					<Tooltip
						position="left"
						content={<ShortcutVisualizer shortcut={getShortcutString(EDITOR2_SHORTCUTS.PUBLISH)} />}
					>
						<DropdownItem
							key="ellipsis-publish-button"
							onClick={validatePageAndOpenModal('LivePagesEllipsisPublishItem')}
						>
							<FormattedMessage {...livePagesi18n.publishDots} />
						</DropdownItem>
					</Tooltip>
				</SpotlightTarget>
				<PublishButtonSpotlightLoader />
			</SpotlightManager>
		);
	},
);

export const ViewChangesItem: FC<ViewChangesItemProps> = ({ contentId, dialogs, saveDraft }) => {
	const intl = useIntl();

	const onClick = useCallback(() => {
		saveDraft?.();
		dialogs.showModal(ViewChangesDialogLoader, {
			contentId,
			title: intl.formatMessage(livePagesi18n.viewChangesTitle),
		});
	}, [contentId, dialogs, intl, saveDraft]);

	return (
		<DropdownItem key="view-changes" onClick={onClick}>
			<FormattedMessage {...i18n.viewChanges} />
		</DropdownItem>
	);
};

export const EditLabelItem = ({ contentId, spaceKey, dialogs }) => (
	<EditLabelDropdownItem contentId={contentId || ''} spaceKey={spaceKey} dialogs={dialogs} />
);

export const ResolvedCommentsItem = ({ contentId, spaceKey, dialogs }) => {
	const { showCommentsPanel } = useShowCommentsPanel();

	return (
		<DropdownItem
			key="resolved-comments"
			onClick={() => {
				if (fg('confluence-frontend-comments-panel')) {
					showCommentsPanel({ openWithView: ViewValues.RESOLVED });
					return;
				}

				dialogs.showModal(ResolvedInlineCommentsDialogLoader, {
					spaceKey,
					contentId,
					context: 'editor',
				});
			}}
		>
			<Box xcss={menuItemContainerStyles}>
				<FormattedMessage {...i18n.resolvedComments} />
				<Box xcss={resolvedCommentCountStyles}>
					<ResolvedInlineCommentsCounterLoader contentId={contentId} />
				</Box>
			</Box>
		</DropdownItem>
	);
};

export const MovePageItem = ({ contentId, spaceKey, retrieveEditorTitleAndContent }) => (
	<EditorMovePageButtonLoader
		contentId={contentId}
		spaceKey={spaceKey}
		appearance="dropdownItem"
		analyticsSource={analyticsSource}
		key="move-page-button"
		retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
		isUnpublishedDraft={false}
	/>
);

export const CopyPageItem = ({ contentId, spaceKey, dialogs }) => (
	<DropdownItem
		key="copy-page"
		onClick={() => {
			dialogs.showModal(CopyPageTreeDialog, {
				contentId,
				spaceKey,
			});
		}}
	>
		<FormattedMessage {...livePagesi18n.copy} />
	</DropdownItem>
);

export const ExportMenuItem = ({
	webItems,
	isSubMenuOpen,
	handleMouseEnter,
	handleClick,
	onOpenChange,
}) => {
	const exportItems = webItems.filter(
		(item) => item.id === 'action-export-pdf-link' || item.id === 'action-export-word-link',
	);

	return (
		<ExpandableSubMenuItem
			label={<FormattedMessage {...livePagesi18n.export} />}
			items={exportItems}
			isSubMenuOpen={isSubMenuOpen}
			onMouseEnter={handleMouseEnter}
			onClick={handleClick}
			onOpenChange={onOpenChange}
		/>
	);
};

export const PageHistoryMenuItem = ({ webItems }) => {
	const historyLinkItem = webItems.find((item) => item.id === 'action-view-history-link');

	if (!historyLinkItem) return null;

	return (
		<DropdownItem key="page-history" href={historyLinkItem.url || ''}>
			{historyLinkItem.label}
		</DropdownItem>
	);
};

export const AttachmentsMenuItem: FC<AttachmentsMenuItemProps> = ({
	contentId,
	spaceKey,
	webItems,
	isSubMenuOpen,
	handleMouseEnter,
	handleClick,
	onOpenChange,
}) => {
	const intl = useIntl();

	const attachmentsItem = webItems.find((item) => item.id === 'view-attachments-link') as
		| ExpandableSubMenuItemItemType
		| undefined;

	const items: ExpandableSubMenuItemItemType[] = [];

	if (attachmentsItem) {
		// extract attachments count from label
		const attachmentsCountStr = attachmentsItem?.label?.match(numbersRegex)?.[0] ?? '';
		attachmentsItem.badgeLabel = attachmentsCountStr;
		// relabel attachments to files
		attachmentsItem.label = intl.formatMessage(livePagesi18n.files);
		items.push(attachmentsItem);
		const sectionItemForCustomContents: ExpandableSubMenuItemItemType = {
			id: 'view-custom-contents-link',
			label: intl.formatMessage(livePagesi18n.customContents),
			url: CUSTOM_CONTENTS_ON_PAGE_LIST.toUrl({
				contentId,
				spaceKey,
			}),
		};
		items.push(sectionItemForCustomContents);
	}

	return (
		<ExpandableSubMenuItem
			label={<FormattedMessage {...livePagesi18n.attachments} />}
			items={items}
			isSubMenuOpen={isSubMenuOpen}
			onMouseEnter={handleMouseEnter}
			onClick={handleClick}
			onOpenChange={onOpenChange}
		/>
	);
};

export const DeletePageItem = ({ contentId, dialogs, webItems }) => {
	const deleteLinkItem = webItems.find((item) => item.id === 'action-remove-content-link');
	if (!deleteLinkItem) return null;

	const contentType = 'page';

	const handleDeleteClick = () => {
		dialogs.showModal(DeleteNestedPagesActionLoader, {
			contentId,
			source: analyticsSource,
			shouldRedirectOnSuccess: true,
			contentType,
		});
	};

	return (
		<DropdownItem key="delete" onClick={handleDeleteClick}>
			{deleteLinkItem.label}
		</DropdownItem>
	);
};

export const AnalyticsItem = ({ webItems }) => {
	const analyticsLinkItem = webItems.find((item) => item.id === ANALYTICS_ADDON_ACTIONS_MENU_ID);

	if (!analyticsLinkItem) return null;

	// Replace the WebItem URL with the new native UI route URL while keeping the query params
	const url = pageAnalyticsUrlToRouteUrl(analyticsLinkItem.url);

	return (
		<AnalyticsPluginWrapper webItemId={analyticsLinkItem.id} key={analyticsLinkItem.completeKey}>
			<DropdownItem href={url} {...analyticsLinkItem}>
				{analyticsLinkItem.label}
			</DropdownItem>
		</AnalyticsPluginWrapper>
	);
};

export const ChangePageOwnershipItem = ({
	webItems,
	contentId,
	closeMenu,
	isOpen,
	onClick,
	onMouseEnter,
	closePopup,
}) => {
	const pageOwnerItem = webItems.find((item) => item.id === CONTENT_OWNERSHIP_WEB_ITEM_ID);
	if (!pageOwnerItem) return null;

	return (
		<ContentOwnerPopup
			contentId={contentId}
			itemId={pageOwnerItem.id}
			itemLabel={pageOwnerItem.label}
			onCloseParent={closeMenu}
			isOpen={isOpen}
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onClosePopup={closePopup}
		/>
	);
};

export const AdvancedDetailsMenuItem = ({
	webItems,
	isSubMenuOpen,
	handleMouseEnter,
	handleClick,
	onOpenChange,
}) => {
	const ids = [
		'link-to-page-link',
		'view-page-info-link',
		'action-view-source-link',
		'view-in-hierarchy-link',
		'action-view-storage-link',
		'content-api',
		'import-word-doc',
		'end-of-page-recommendation-menu',
	];
	const subItems = webItems.filter((item) => ids.includes(item.id));

	return (
		<ExpandableSubMenuItem
			label={<FormattedMessage {...livePagesi18n.advanced} />}
			items={subItems}
			isSubMenuOpen={isSubMenuOpen}
			onMouseEnter={handleMouseEnter}
			onClick={handleClick}
			onOpenChange={onOpenChange}
		/>
	);
};

export const ArchiveLinkMenuItem = ({ webItems, contentId, dialogs }) => {
	const archiveLinkId = 'action-archive-link';
	const archiveLinkItem = webItems.find((item) => item.id === archiveLinkId);
	const handleArchiveMenuClick = (edition: ConfluenceEdition | null) => {
		if (edition !== ConfluenceEdition.FREE) {
			dialogs.showModal(ArchivePagesLoader, {
				contentId,
				source: 'contentToolsItem',
			});
		} else {
			dialogs.showModal(ArchiveUpsellDialog);
		}
	};

	return (
		<SessionData>
			{({ edition }) => (
				<Query<any, any> query={ShowArchiveCTAQuery} variables={{ contentId }}>
					{({ loading, error, data }) => {
						const operations = data?.content?.nodes?.[0]?.operations;
						if (loading) {
							return null;
						} else if (error) {
							return <ErrorDisplay error={error} />;
						} else if (
							edition === ConfluenceEdition.FREE ||
							(operations !== null && canUnarchivePage(operations))
						) {
							return (
								<DropdownItem
									key={archiveLinkItem.completeKey}
									onClick={() => handleArchiveMenuClick(edition)}
								>
									<FormattedMessage {...livePagesi18n.archive} />
								</DropdownItem>
							);
						} else {
							return null;
						}
					}}
				</Query>
			)}
		</SessionData>
	);
};

export const TemplateMenuItem = ({ onClickOpenContentCreationPanel }) => (
	<DropdownItem onClick={onClickOpenContentCreationPanel}>
		<FormattedMessage {...i18n.templateImportDocs} />
	</DropdownItem>
);

export const PageAppsMenuItem = ({
	webItems,
	isSubMenuOpen,
	handleMouseEnter,
	handleClick,
	forgeMenuItems,
	onOpenChange,
}) => (
	<PageAppsExpandableSubMenuItems
		label={<FormattedMessage {...livePagesi18n.pageApps} />}
		items={webItems}
		isSubMenuOpen={isSubMenuOpen}
		onMouseEnter={handleMouseEnter}
		onClick={handleClick}
		forgeMenuItems={forgeMenuItems}
		onOpenChange={onOpenChange}
	/>
);

export const useEnterPresenterModeAndStartExperience = (contentId) => {
	const spaceId = useSpaceId();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const [{ theme }] = useThemingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return ({
			enterPresenterMode,
			isTriggedByKeyboardShortcut = false,
		}: {
			enterPresenterMode: () => void;
			isTriggedByKeyboardShortcut?: boolean;
		}) =>
		() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'present',
					source: 'LivePagesEllipsisMenuItems',
					objectId: contentId,
					containerId: spaceId,
					attributes: {
						currentThemePreference: theme,
						OStheme: getSystemPreferredColorScheme(),
						isTriggedByKeyboardShortcut,
					},
				},
			}).fire();

			experienceTracker.start({
				name: PRESENTER_MODE_VIEW_EXPERIENCE,
			});

			enterPresenterMode();
		};
};

export const PresenterModeItem = ({ webItems, contentId }) => {
	const presenterModeId = 'presentation-mode';
	const presenterModeItem = webItems.find((item) => item.id === presenterModeId);

	const enterPresenterModeAndStartExperience = useEnterPresenterModeAndStartExperience(contentId);

	if (!presenterModeItem) return null;

	return (
		<ErrorBoundary attribution={Attribution.COMMENTS} key={presenterModeItem.completeKey}>
			<LazyPresenterModeContextConsumer
				key={`presenter-mode-menu-item-${presenterModeItem.completeKey}`}
			>
				{(enterPresenterMode) => (
					<DropdownItem
						key={presenterModeId}
						onClick={enterPresenterModeAndStartExperience({ enterPresenterMode })}
						elemBefore={<PresenterModeIcon label="" />}
					>
						<FormattedMessage {...livePagesi18n.presenterMode} />
					</DropdownItem>
				)}
			</LazyPresenterModeContextConsumer>
		</ErrorBoundary>
	);
};
